import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, ArrowRight } from "lucide-react";
import { useStore } from "../context/StoreContext";
import api, { img, money } from "../lib/api";
import { toast } from "sonner";

export default function Cart() {
  const { cart, updateQty, removeFromCart, settings } = useStore();
  const nav = useNavigate();
  const [coupon, setCoupon] = useState("");
  const [summary, setSummary] = useState(null);

  const revalidate = (code) => {
    if (cart.length === 0) { setSummary(null); return; }
    api.post("/checkout/validate", { items: cart.map(i => ({ slug: i.slug, quantity: i.quantity })), coupon_code: code })
      .then(r => setSummary(r.data)).catch(() => {});
  };
  useEffect(() => { revalidate(coupon || null); }, [cart]);

  const applyCoupon = () => {
    api.post("/checkout/validate", { items: cart.map(i => ({ slug: i.slug, quantity: i.quantity })), coupon_code: coupon })
      .then(r => { setSummary(r.data); r.data.coupon_code ? toast.success("Coupon applied") : toast.error("Invalid coupon or minimum not met"); });
  };

  if (cart.length === 0) return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-32 text-center">
      <p className="serif text-5xl text-forest/70 mb-6">Your ritual is waiting.</p>
      <Link to="/shop" className="btn-primary" data-testid="cart-page-shop">Explore Collection</Link>
    </div>
  );

  const threshold = settings.free_shipping_threshold || 999;
  const sub = summary?.subtotal ?? 0;
  const remaining = Math.max(0, threshold - (sub - (summary?.discount || 0)));

  return (
    <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-14">
      <h1 className="serif text-5xl mb-10">Your Cart</h1>
      <div className="grid lg:grid-cols-[1fr_380px] gap-12">
        <div>
          {remaining > 0 ? <p className="text-sm text-forest/70 mb-6 pb-6 border-b border-sage/30">You're <strong>{money(remaining)}</strong> away from free shipping.</p>
            : <p className="text-sm text-forest mb-6 pb-6 border-b border-sage/30">You've unlocked free shipping.</p>}
          <div className="divide-y divide-sage/20">
            {cart.map((it) => (
              <div key={it.slug} className="flex gap-5 py-6" data-testid={`cartpage-item-${it.slug}`}>
                <Link to={`/product/${it.slug}`}><img src={img(it.image)} alt={it.name} className="h-32 w-24 object-cover bg-beige/40" /></Link>
                <div className="flex-1">
                  <div className="flex justify-between">
                    <div><Link to={`/product/${it.slug}`} className="serif text-xl">{it.name}</Link><div className="text-xs text-forest/50">{it.size}</div></div>
                    <button onClick={() => removeFromCart(it.slug)} className="text-forest/40 hover:text-forest" data-testid={`cartpage-remove-${it.slug}`}><Trash2 size={16} /></button>
                  </div>
                  <div className="flex items-center justify-between mt-6">
                    <div className="flex items-center border border-forest/25">
                      <button onClick={() => updateQty(it.slug, it.quantity - 1)} className="px-3 py-2"><Minus size={13} /></button>
                      <span className="px-4 text-sm">{it.quantity}</span>
                      <button onClick={() => updateQty(it.slug, it.quantity + 1)} className="px-3 py-2"><Plus size={13} /></button>
                    </div>
                    <span className="font-medium">{money(it.price * it.quantity)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <aside className="bg-beige/40 p-8 h-fit lg:sticky lg:top-28">
          <h2 className="serif text-2xl mb-6">Order Summary</h2>
          <div className="flex gap-2 mb-6">
            <input value={coupon} onChange={(e) => setCoupon(e.target.value)} placeholder="Coupon code" data-testid="coupon-input"
              className="flex-1 border border-forest/20 bg-ivory px-3 py-2.5 text-sm outline-none uppercase" />
            <button onClick={applyCoupon} data-testid="coupon-apply" className="bg-forest text-ivory px-5 text-xs uppercase tracking-wide">Apply</button>
          </div>
          <div className="space-y-3 text-sm border-t border-sage/30 pt-5">
            <Row label="Subtotal" value={money(summary?.subtotal || 0)} />
            {summary?.discount > 0 && <Row label={`Discount (${summary.coupon_code})`} value={`− ${money(summary.discount)}`} />}
            <Row label="Shipping" value={summary?.shipping ? money(summary.shipping) : "Free"} />
            {summary?.tax > 0 && <Row label="Taxes" value={money(summary.tax)} />}
            <div className="flex justify-between pt-4 border-t border-sage/30"><span className="serif text-xl">Total</span><span className="serif text-2xl" data-testid="cart-total">{money(summary?.total || 0)}</span></div>
          </div>
          <button onClick={() => nav("/checkout")} className="btn-primary w-full mt-6 flex items-center justify-center gap-2" data-testid="cart-proceed-checkout">Proceed to Checkout <ArrowRight size={15} /></button>
          <Link to="/shop" className="block text-center text-xs uppercase tracking-[0.14em] text-forest/60 mt-4 hover:text-forest">Continue Shopping</Link>
        </aside>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex justify-between"><span className="text-forest/65">{label}</span><span className="text-forest">{value}</span></div>
);
